import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { StepWelcome } from '@/components/Onboarding/StepWelcome';
import { StepBudget } from '@/components/Onboarding/StepBudget';
import { StepCurrency } from '@/components/Onboarding/StepCurrency';
import { StepMotivation } from '@/components/Onboarding/StepMotivation';
import { ProgressIndicator } from '@/components/Onboarding/ProgressIndicator';
import { trackEvent } from '@/lib/analytics';
import type { MotivationStyle } from '@/contexts/GamificationContext';
import { useLanguage } from '@/contexts/LanguageContext';

export default function Onboarding() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [name, setName] = useState('');
  const [budget, setBudget] = useState(1000);
  const [currency, setCurrency] = useState('EUR');
  const [motivationStyle, setMotivationStyle] = useState<MotivationStyle>('gentle');

  const handleComplete = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Save budget settings including motivation style
      // When motivationStyle is 'minimal', also enable hide_rewards (calm mode)
      const hideRewards = motivationStyle === 'minimal';
      
      await supabase.from('budget_settings').upsert({
        user_id: user.id,
        monthly_budget: budget,
        currency: currency,
        motivation_style: motivationStyle,
        hide_rewards: hideRewards,
        coachmark_points_seen: false,
      });

      // Save name to localStorage if provided
      if (name) {
        localStorage.setItem('userName', name);
      }

      // Mark onboarding as complete
      localStorage.setItem('onboardingComplete', 'true');

      // Track completion
      trackEvent({
        name: 'onboarding_completed',
        props: { budget, currency, dark: false, calm: hideRewards }
      });

      navigate('/');
    } catch (error) {
      console.error('Onboarding error:', error);
    }
  };

  const handleSkip = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Set defaults
        await supabase.from('budget_settings').upsert({
          user_id: user.id,
          monthly_budget: 1000,
          currency: 'EUR',
        });
      }
      localStorage.setItem('onboardingComplete', 'true');
      navigate('/');
    } catch (error) {
      console.error('Skip error:', error);
      navigate('/');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <StepWelcome
            onNext={(userName) => {
              setName(userName);
              setCurrentStep(1);
            }}
            onSkip={handleSkip}
          />
        );
      case 1:
        return (
          <StepMotivation
            onNext={(style) => {
              setMotivationStyle(style);
              setCurrentStep(2);
            }}
            onSkip={handleSkip}
          />
        );
      case 2:
        return (
          <StepCurrency
            onNext={(curr) => {
              setCurrency(curr);
              setCurrentStep(3);
            }}
            onSkip={handleSkip}
          />
        );
      case 3:
        return (
          <StepBudget
            currency={currency}
            onNext={(amount) => {
              setBudget(amount);
              handleComplete();
            }}
            onSkip={handleSkip}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background p-6 flex items-center justify-center">
      <div className="w-full max-w-md">
        <ProgressIndicator currentStep={currentStep} totalSteps={4} />
        {renderStep()}
        <p className="text-[10px] text-muted-foreground/60 leading-relaxed text-center mt-6 mb-4">
          {t('onboarding.welcome.legal')}
        </p>
      </div>
    </div>
  );
}
